/**
 * chart-kit.js — one charting behaviour for the whole app.
 *
 * The dashboard's trend chart was the only chart with real interactions: a full-band hit
 * target, a tooltip carrying every series at once, an emphasised mark, a crosshair, and a
 * drill-through. Everything else got whatever its route happened to write. Ad Performance's
 * "Spend vs Leads" is a structural near-clone of the dashboard chart that shipped with
 * native <title> tooltips — the exact mechanism the dashboard removed for being slow,
 * one-value-at-a-time and unusable on touch — and Analytics' by-magnet bar had no hover at
 * all. Same picture, three answers.
 *
 * WHY CLASSES AND NOT ATTRIBUTES FOR COLOUR. SVG presentation attributes cannot resolve
 * var(), so `fill="#0072BA"` is the only thing that works inline — and it is also why
 * Ad Performance was invisible-to-wrong in dark mode: five literal light-theme hexes that
 * never followed the theme. Every mark here takes its colour from a class backed by the
 * --chart-* tokens in launCh.css, which are defined per theme.
 *
 * WHY .m5c-* AND NOT .dash-*. The classes moved out of dashboard-route.js with the code.
 * Leaving them named after one page while three pages use them is how the next person
 * concludes this belongs to the dashboard and writes a fourth chart.
 *
 * KEYBOARD IS PART OF THE COMPONENT, not a later pass. The dashboard's drill-through was
 * mouse-only: the hit bands had no tabindex, no focus handling and no key bindings, so the
 * only way to reach a day's leads from the chart was to point at it. Anything built on this
 * kit gets arrow-key traversal, focus-driven tooltips and Enter/Space activation for free,
 * so that gap cannot be inherited by the charts adopting it.
 */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
export const fmtNum = (n) => Number(n || 0).toLocaleString();

/** Round an axis maximum up to a friendly 1/2/5 x 10^n. */
export function niceMax(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

/** Compact axis label: 1200 -> "1.2k". */
export const fmtAxis = (v) => {
  const r = Math.round(v);
  return r >= 1000 ? `${(r / 1000).toFixed(r % 1000 === 0 ? 0 : 1)}k` : String(r);
};

let stylesInjected = false;

/**
 * Inject the shared chart CSS once. Every colour resolves through a --chart-* token, so
 * charts follow the theme instead of pinning one theme's hexes.
 */
export function ensureChartStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5c-chart-styles';
  s.textContent = `
    .m5c-wrap { position: relative; }
    .m5c-chart { width: 100%; height: auto; display: block; }
    .m5c-legend { display: flex; gap: 16px; margin: 0 0 8px; font-size: 0.8rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); }
    .m5c-legend i { display: inline-block; width: 11px; height: 11px; border-radius: 3px; margin-right: 5px; vertical-align: middle; }
    text.m5c-ax { font-size: 11px; fill: var(--color-text-subtle, #6C7C9A); }
    line.m5c-grid { stroke: var(--chart-grid, #EAF0F7); }

    /* Marks sit at 0.85 so the active one can go to full without a hue change — a
       brightness shift reads as "this one" more cleanly and keeps the legend honest. */
    .m5c-bar { fill: var(--chart-1, #0072BA); opacity: 0.85; transition: opacity .12s ease; }
    .m5c-bar--on { opacity: 1; }
    .m5c-line { stroke: var(--chart-2, #009473); }
    .m5c-dot { fill: var(--chart-2, #009473); transition: r .12s ease, stroke-width .12s ease; }
    /* Ring lifts the active dot off the line it sits on. */
    .m5c-dot--on { r: 5.2; stroke: var(--color-bg, #fff); stroke-width: 2; }
    .m5c-sw--1 { background: var(--chart-1, #0072BA); }
    .m5c-sw--2 { background: var(--chart-2, #009473); }

    .m5c-hit { cursor: pointer; fill: transparent; transition: fill .12s ease; }
    .m5c-hit--on { fill: color-mix(in srgb, var(--chart-1, #0072BA) 6%, transparent); }
    /* A point with nothing to drill into must not offer to. */
    .m5c-hit[data-activatable="false"] { cursor: default; }
    /* Keyboard focus has to be visible on a transparent target, or tabbing through the
       chart looks like nothing is happening. */
    .m5c-hit:focus { outline: none; }
    .m5c-hit:focus-visible { fill: color-mix(in srgb, var(--chart-1, #0072BA) 12%, transparent); stroke: var(--chart-1, #0072BA); stroke-width: 1.5; }
    .m5c-crosshair { stroke: var(--color-text-subtle, #6C7C9A); stroke-width: 1; stroke-dasharray: 3 3; opacity: 0; transition: opacity .12s ease; }
    .m5c-crosshair--on { opacity: 0.55; }

    .m5c-tip { position: absolute; transform: translate(-50%, -100%) translateY(-8px); pointer-events: none; z-index: 5; background: var(--chart-tip-bg, #0B1220); color: var(--chart-tip-text, #F2F7FB); padding: 8px 10px; border-radius: 8px; font-size: 0.78rem; line-height: 1.5; white-space: nowrap; box-shadow: var(--shadow-md, 0 8px 24px rgba(11,18,32,0.18)); }
    .m5c-tip[hidden] { display: none; }
    .m5c-tip__title { font-weight: 700; margin-bottom: 3px; }
    .m5c-tip__row { display: flex; align-items: center; gap: 6px; }
    .m5c-tip__row i { display: inline-block; width: 8px; height: 8px; border-radius: 2px; flex: 0 0 auto; }
    .m5c-tip__go { margin-top: 5px; padding-top: 5px; border-top: 1px solid rgba(255,255,255,0.18); font-weight: 700; color: var(--chart-tip-link, #7BB7FF); }
    [data-theme="dark"] .m5c-tip { border: 1px solid var(--color-border-strong, #3D6A8F); }

    html.ff-no-animations .m5c-bar, html.ff-no-animations .m5c-dot,
    html.ff-no-animations .m5c-hit, html.ff-no-animations .m5c-crosshair { transition: none; }
    @media (prefers-reduced-motion: reduce) {
      .m5c-bar, .m5c-dot, .m5c-hit, .m5c-crosshair { transition: none; }
    }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

/**
 * Render a dual-axis chart: bars on the left axis, a line with dots on the right.
 *
 * Both the dashboard's opens-vs-leads and ad-performance's spend-vs-leads are this shape;
 * they were separate 40-line copies with their own duplicate niceMax/fmtAxis.
 *
 * @param {object} opts
 * @param {Array} opts.rows
 * @param {(row:object, i:number) => string} opts.xLabel - x-axis tick text
 * @param {{ value:(row)=>number, label:string, fmt?:(v)=>string }} opts.left  - bars
 * @param {{ value:(row)=>number, label:string, fmt?:(v)=>string }} opts.right - line + dots
 * @param {string} opts.ariaLabel - the chart is one image to assistive tech
 * @param {(row:object) => boolean} [opts.activatable] - may this point be drilled into?
 * @param {(row:object, i:number) => string} [opts.pointLabel] - a11y label per hit band
 * @param {(row:object) => object} [opts.hitData] - extra data-* attrs per band, so a band
 *   stays self-describing for anything reading the DOM (tests, debugging) even though the
 *   handlers themselves read the rows closure
 * @returns {string} HTML
 */
export function dualAxisChartHTML({
  rows = [], xLabel = () => '', left, right, ariaLabel = 'Chart',
  activatable = () => false, pointLabel = () => '', hitData = () => ({}),
}) {
  const W = 840, H = 300, ml = 56, mr = 46, mt = 18, mb = 34;
  const x0 = ml, x1 = W - mr, y0 = mt, y1 = H - mb;
  const plotH = y1 - y0, plotW = x1 - x0;
  const n = rows.length || 1;
  const lfmt = left.fmt || fmtAxis;
  const rfmt = right.fmt || fmtAxis;
  const maxL = niceMax(Math.max(1, ...rows.map((r) => left.value(r) || 0)));
  const maxR = niceMax(Math.max(1, ...rows.map((r) => right.value(r) || 0)));
  const band = plotW / n;
  const bw = Math.min(22, band * 0.55);
  const cx = (i) => x0 + band * (i + 0.5);
  const lY = (v) => y1 - (v / maxL) * plotH;
  const rY = (v) => y1 - (v / maxR) * plotH;

  const grid = [0, 0.5, 1].map((t) => {
    const y = y1 - t * plotH;
    return `<line x1="${x0}" y1="${y.toFixed(1)}" x2="${x1}" y2="${y.toFixed(1)}" class="m5c-grid"/>`
      + `<text x="${x0 - 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="end" class="m5c-ax">${esc(lfmt(maxL * t))}</text>`
      + `<text x="${x1 + 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="start" class="m5c-ax">${esc(rfmt(maxR * t))}</text>`;
  }).join('');

  // data-i lives on the MARKS as well as the bands: the bands are painted last so they can
  // catch pointer events across a whole column, and a sibling combinator only reaches
  // forward, so CSS alone can never emphasise the bar a band sits over.
  const bars = rows.map((r, i) => {
    const y = lY(left.value(r) || 0);
    return `<rect class="m5c-bar" data-i="${i}" x="${(cx(i) - bw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, y1 - y).toFixed(1)}" rx="3"/>`;
  }).join('');
  const pts = rows.map((r, i) => `${cx(i).toFixed(1)},${rY(right.value(r) || 0).toFixed(1)}`).join(' ');
  const line = n > 1 ? `<polyline class="m5c-line" points="${pts}" fill="none" stroke-width="2.5"/>` : '';
  const dots = rows.map((r, i) => `<circle class="m5c-dot" data-i="${i}" cx="${cx(i).toFixed(1)}" cy="${rY(right.value(r) || 0).toFixed(1)}" r="3.2"/>`).join('');
  // One read-line moved by the handler, not one per column — nothing to clean up as the
  // pointer (or focus) crosses columns.
  const crosshair = `<line class="m5c-crosshair" x1="0" y1="${y0}" x2="0" y2="${y1}" pointer-events="none"/>`;
  const step = Math.max(1, Math.ceil(n / 6));
  const xlabels = rows.map((r, i) => ((i % step === 0 || i === n - 1)
    ? `<text x="${cx(i).toFixed(1)}" y="${H - 12}" text-anchor="middle" class="m5c-ax">${esc(xLabel(r, i))}</text>` : '')).join('');

  // Full-band transparent targets, so pointing anywhere in a column reads that column
  // rather than only the pixels a bar or dot covers. tabindex makes the same targets
  // reachable without a pointer — the dashboard's drill-through was mouse-only.
  const hits = rows.map((r, i) => {
    const can = !!activatable(r);
    const extra = Object.entries(hitData(r) || {})
      .map(([k, v]) => ` data-${esc(k)}="${esc(v)}"`).join('');
    return `<rect class="m5c-hit" data-i="${i}" data-activatable="${can}" tabindex="0" role="button"`
      + ` aria-label="${esc(pointLabel(r, i))}"${extra}`
      + ` x="${(x0 + band * i).toFixed(1)}" y="${y0}" width="${band.toFixed(1)}" height="${plotH.toFixed(1)}" fill="transparent"/>`;
  }).join('');

  return `
    <div class="m5c-legend">
      <span><i class="m5c-sw m5c-sw--1"></i>${esc(left.label)}</span>
      <span><i class="m5c-sw m5c-sw--2"></i>${esc(right.label)}</span>
    </div>
    <div class="m5c-wrap">
      <svg class="m5c-chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${esc(ariaLabel)}">
        ${grid}${crosshair}${bars}${line}${dots}${xlabels}${hits}
      </svg>
      <div class="m5c-tip" role="status" hidden></div>
    </div>`;
}

/** True when the device has a real hover pointer. Absent matchMedia (jsdom) = desktop. */
export function canHover() {
  try { return !window.matchMedia || window.matchMedia('(hover: hover)').matches; } catch { return true; }
}

/**
 * Wire hover, focus, keyboard and activation for a chart rendered by dualAxisChartHTML.
 *
 * DELEGATED, unlike the per-band listeners this replaces. Routes repaint these containers
 * (a period switch rebuilds the whole chart), and per-element handlers have to be
 * re-attached every time — one listener on the wrapper survives any repaint.
 *
 * @param {ParentNode} root - element containing the .m5c-wrap
 * @param {object} opts
 * @param {Array} opts.rows - the same array the markup was built from; read from this
 *   closure rather than parsed back out of DOM attributes
 * @param {(row:object, i:number) => string} opts.tipHTML
 * @param {(row:object, i:number) => void} [opts.onActivate] - drill-through
 * @param {(row:object, i:number) => void} [opts.onShow] - a column is being read (hover,
 *   focus or tap); lets a page mirror that column's values elsewhere, e.g. a KPI strip
 * @param {() => void} [opts.onHide] - the reading ended; restore whatever onShow changed
 * @returns {() => void} cleanup
 */
export function wireChartHover(root, { rows = [], tipHTML, onActivate, onShow, onHide } = {}) {
  if (!root?.addEventListener || typeof tipHTML !== 'function') return () => {};

  // Listeners bind to ROOT, not to .m5c-wrap. Routes repaint by assigning innerHTML on the
  // host, which REPLACES the wrap — so anything bound to the wrap is left on a detached
  // node and the chart goes dead after the first period switch. Everything below therefore
  // resolves the wrap and tooltip per event rather than closing over them.
  const wrapOf = () => root.querySelector('.m5c-wrap');
  const tipOf = () => root.querySelector('.m5c-tip');

  const clearMarks = (wrap) => {
    wrap.querySelectorAll('.m5c-bar--on, .m5c-dot--on, .m5c-hit--on')
      .forEach((el) => el.classList.remove('m5c-bar--on', 'm5c-dot--on', 'm5c-hit--on'));
    wrap.querySelector('.m5c-crosshair')?.classList.remove('m5c-crosshair--on');
  };

  // Emphasis is a separate concern from the tooltip: the tooltip says what the value IS,
  // this says which mark it belongs to. Without it the numbers appear beside an unchanged
  // chart and the reader has to eyeball which column they are on.
  const markActive = (wrap, i, hitEl) => {
    clearMarks(wrap);
    wrap.querySelector(`.m5c-bar[data-i="${i}"]`)?.classList.add('m5c-bar--on');
    wrap.querySelector(`.m5c-dot[data-i="${i}"]`)?.classList.add('m5c-dot--on');
    hitEl.classList.add('m5c-hit--on');
    const crosshair = wrap.querySelector('.m5c-crosshair');
    if (crosshair) {
      // Band centre in the SVG's own viewBox units — the space the marks were laid out in,
      // so it stays aligned at any rendered width.
      const bx = Number(hitEl.getAttribute('x')) + Number(hitEl.getAttribute('width')) / 2;
      crosshair.setAttribute('x1', bx);
      crosshair.setAttribute('x2', bx);
      crosshair.classList.add('m5c-crosshair--on');
    }
  };

  const show = (i, hitEl) => {
    const wrap = wrapOf(); const tip = tipOf();
    const row = rows[i];
    if (!wrap || !tip || !row || !hitEl) return;
    tip.innerHTML = tipHTML(row, i);
    const hitRect = hitEl.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    let left = hitRect.left - wrapRect.left + hitRect.width / 2;
    // Clamp inside the wrapper. Without this the first and last columns push the tooltip
    // past the edge of the card it lives in, which is where it is least readable.
    const half = (tip.offsetWidth || 0) / 2;
    if (half) left = Math.min(Math.max(left, half + 4), Math.max(half + 4, wrapRect.width - half - 4));
    tip.style.left = `${left}px`;
    tip.style.top = `${hitRect.top - wrapRect.top}px`;
    tip.hidden = false;
    markActive(wrap, i, hitEl);
    if (typeof onShow === 'function') { try { onShow(row, i); } catch { /* a mirror must never break the chart */ } }
  };
  const hide = () => {
    const wrap = wrapOf(); const tip = tipOf();
    if (tip) tip.hidden = true;
    if (wrap) clearMarks(wrap);
    if (typeof onHide === 'function') { try { onHide(); } catch { /* same */ } }
  };

  const hitFrom = (e) => {
    const el = e.target.closest?.('.m5c-hit');
    return el && root.contains(el) ? el : null;
  };
  const idxOf = (el) => Number(el?.dataset?.i);
  const activate = (el) => {
    const i = idxOf(el);
    const row = rows[i];
    if (!row || el.dataset.activatable !== 'true' || typeof onActivate !== 'function') return;
    onActivate(row, i);
  };

  const onOver = (e) => { const el = hitFrom(e); if (el) show(idxOf(el), el); };
  const onOut = (e) => {
    // Moving to another band inside the same chart is not a leave.
    const to = e.relatedTarget;
    if (to && root.contains(to) && to.closest?.('.m5c-hit')) return;
    hide();
  };
  const onClick = (e) => {
    const el = hitFrom(e);
    if (!el) return;
    const wasActive = el.classList.contains('m5c-hit--on');
    show(idxOf(el), el);
    // With hover, the tooltip was already shown on mouseover, so a click is a considered
    // second action and navigates. Without hover the first tap is the only way to READ the
    // values, so it previews and the second tap on the same point activates. Gated on
    // matchMedia rather than on "already active", because some touch browsers synthesise
    // mouseover before click and would otherwise steal the reading.
    if (!canHover() && !wasActive) return;
    activate(el);
  };
  // Focus drives the same tooltip as hover, so the chart is readable by keyboard rather
  // than merely reachable.
  const onFocus = (e) => { const el = hitFrom(e); if (el) show(idxOf(el), el); };
  const onBlur = (e) => {
    const to = e.relatedTarget;
    if (to && root.contains(to) && to.closest?.('.m5c-hit')) return;
    hide();
  };
  const onKeyDown = (e) => {
    const el = hitFrom(e);
    if (!el) return;
    const all = [...root.querySelectorAll('.m5c-hit')];
    const i = all.indexOf(el);
    const go = (j) => {
      const next = all[Math.max(0, Math.min(all.length - 1, j))];
      if (!next) return;
      e.preventDefault();
      next.focus?.();
      show(idxOf(next), next);
    };
    switch (e.key) {
      case 'Enter': case ' ': case 'Spacebar':
        e.preventDefault(); activate(el); break;
      case 'ArrowRight': go(i + 1); break;
      case 'ArrowLeft': go(i - 1); break;
      case 'Home': go(0); break;
      case 'End': go(all.length - 1); break;
      case 'Escape': hide(); el.blur?.(); break;
      default: break;
    }
  };

  root.addEventListener('mouseover', onOver);
  root.addEventListener('mouseout', onOut);
  root.addEventListener('click', onClick);
  root.addEventListener('focusin', onFocus);
  root.addEventListener('focusout', onBlur);
  root.addEventListener('keydown', onKeyDown);

  return () => {
    root.removeEventListener('mouseover', onOver);
    root.removeEventListener('mouseout', onOut);
    root.removeEventListener('click', onClick);
    root.removeEventListener('focusin', onFocus);
    root.removeEventListener('focusout', onBlur);
    root.removeEventListener('keydown', onKeyDown);
  };
}

export default {
  ensureChartStyles, dualAxisChartHTML, wireChartHover, niceMax, fmtAxis, canHover,
};
